// Settings sidebar — wires DOM inputs to a typed AppConfig and notifies the
// app via callbacks. No framework; just direct DOM manipulation.

import { COCO_CLASSES, DEFAULT_CONFIG, type AppConfig } from "../inference/types";

export type ConfigChangeReason =
  | "confidence"
  | "classes"
  | "zone-toggle"
  | "alerts-toggle"
  | "persistence"
  | "cooldown"
  | "alert-confidence"
  | "polygon"
  | "device"
  | "capture-fps"
  | "storage-mode"
  | "webhook"
  | "display";

export interface SettingsCallbacks {
  onChange: (config: AppConfig, reason: ConfigChangeReason) => void;
  onClearZone: () => void;
  onExportZip: () => Promise<void>;
  onClearHistory: () => Promise<void>;
  onTestWebhook: () => Promise<void>;
}

export class Settings {
  config: AppConfig = { ...DEFAULT_CONFIG };

  constructor(private cb: SettingsCallbacks) {
    this.wireSliders();
    this.wireToggles();
    this.wireSelects();
    this.wireClassChips();
    this.wireButtons();
    this.wireWebhook();
  }

  setConfig(next: AppConfig): void {
    this.config = next;
    this.reflect();
  }

  private $<T extends HTMLElement>(id: string): T {
    const el = document.getElementById(id) as T | null;
    if (!el) throw new Error(`#${id} not found`);
    return el;
  }

  private wireSliders(): void {
    const wire = (
      id: string,
      valId: string,
      reason: ConfigChangeReason,
      apply: (v: number) => void,
      fmt: (v: number) => string,
    ) => {
      const input = this.$<HTMLInputElement>(id);
      const valEl = this.$<HTMLSpanElement>(valId);
      const update = () => {
        const v = parseFloat(input.value);
        apply(v);
        valEl.textContent = fmt(v);
        this.cb.onChange(this.config, reason);
      };
      input.addEventListener("input", update);
      valEl.textContent = fmt(parseFloat(input.value));
    };

    wire("conf", "conf-val", "confidence",
      (v) => (this.config.confidence_threshold = v),
      (v) => v.toFixed(2));
    wire("persistence", "persistence-val", "persistence",
      (v) => (this.config.min_persistence_seconds = v),
      (v) => v.toFixed(1));
    wire("cooldown", "cooldown-val", "cooldown",
      (v) => (this.config.alert_cooldown_seconds = v),
      (v) => v.toFixed(1));
    wire("alert-conf", "alert-conf-val", "alert-confidence",
      (v) => (this.config.min_alert_confidence = v),
      (v) => v.toFixed(2));
    wire("capture-fps", "capture-fps-val", "capture-fps",
      (v) => (this.config.capture_fps = v),
      (v) => v.toFixed(0));
  }

  private wireToggles(): void {
    const bind = (id: string, reason: ConfigChangeReason, apply: (v: boolean) => void) => {
      const el = this.$<HTMLInputElement>(id);
      el.addEventListener("change", () => {
        apply(el.checked);
        this.cb.onChange(this.config, reason);
      });
    };
    bind("zone-on", "zone-toggle", (v) => (this.config.danger_zone_enabled = v));
    bind("alerts-on", "alerts-toggle", (v) => (this.config.alert_on_danger_zone = v));
    bind("webhook-on", "webhook", (v) => (this.config.webhook_enabled = v));
    bind("show-hud", "display", (v) => (this.config.show_hud = v));
    bind("show-ids", "display", (v) => (this.config.show_track_ids = v));
    bind("show-conf", "display", (v) => (this.config.show_confidence = v));
  }

  private wireWebhook(): void {
    const url = this.$<HTMLInputElement>("webhook-url");
    const key = this.$<HTMLInputElement>("webhook-key");
    // Use "change" (commits on blur / enter) so we don't spam onChange on
    // every keystroke. Localstorage persistence in main.ts hooks into this.
    url.addEventListener("change", () => {
      this.config.webhook_url = url.value.trim();
      this.cb.onChange(this.config, "webhook");
    });
    key.addEventListener("change", () => {
      this.config.webhook_api_key = key.value;
      this.cb.onChange(this.config, "webhook");
    });
    this.$<HTMLButtonElement>("btn-webhook-test").addEventListener("click", async () => {
      // Commit any pending field edits before the test fires.
      this.config.webhook_url = url.value.trim();
      this.config.webhook_api_key = key.value;
      this.cb.onChange(this.config, "webhook");
      await this.cb.onTestWebhook();
    });
  }

  private wireSelects(): void {
    const dev = this.$<HTMLSelectElement>("device-select");
    dev.addEventListener("change", () => {
      this.config.inference_device = dev.value as AppConfig["inference_device"];
      this.cb.onChange(this.config, "device");
    });
    const sm = this.$<HTMLSelectElement>("storage-mode");
    sm.addEventListener("change", () => {
      this.config.storage_mode = sm.value as AppConfig["storage_mode"];
      this.cb.onChange(this.config, "storage-mode");
    });
  }

  private wireClassChips(): void {
    const chips = this.$<HTMLDivElement>("class-chips");
    chips.innerHTML = "";
    const selected = new Set<string>();
    for (const name of COCO_CLASSES) {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "chip";
      chip.dataset["name"] = name;
      chip.textContent = name;
      chip.addEventListener("click", () => {
        if (selected.has(name)) {
          selected.delete(name);
          chip.classList.remove("active");
        } else {
          selected.add(name);
          chip.classList.add("active");
        }
        this.config.classes = selected.size === 0 ? null : [...selected].sort();
        this.cb.onChange(this.config, "classes");
      });
      chips.appendChild(chip);
    }
  }

  private wireButtons(): void {
    this.$<HTMLButtonElement>("btn-zone-clear").addEventListener("click", () => {
      this.config.danger_zone_polygon = [];
      this.cb.onClearZone();
      this.cb.onChange(this.config, "polygon");
    });
    this.$<HTMLButtonElement>("btn-export").addEventListener("click", async () => {
      await this.cb.onExportZip();
    });
    this.$<HTMLButtonElement>("btn-clear").addEventListener("click", async () => {
      if (!confirm("Clear all events + snapshots from this device?")) return;
      await this.cb.onClearHistory();
    });
  }

  /** Push current config back into the DOM (used after programmatic changes). */
  private reflect(): void {
    const c = this.config;
    (this.$<HTMLInputElement>("conf").value as unknown as string) = String(c.confidence_threshold);
    (this.$<HTMLInputElement>("persistence").value as unknown as string) =
      String(c.min_persistence_seconds);
    (this.$<HTMLInputElement>("cooldown").value as unknown as string) =
      String(c.alert_cooldown_seconds);
    (this.$<HTMLInputElement>("alert-conf").value as unknown as string) =
      String(c.min_alert_confidence);
    (this.$<HTMLInputElement>("capture-fps").value as unknown as string) =
      String(c.capture_fps);
    this.$<HTMLInputElement>("zone-on").checked = c.danger_zone_enabled;
    this.$<HTMLInputElement>("alerts-on").checked = c.alert_on_danger_zone;
    this.$<HTMLInputElement>("webhook-on").checked = c.webhook_enabled;
    this.$<HTMLInputElement>("webhook-url").value = c.webhook_url;
    this.$<HTMLInputElement>("webhook-key").value = c.webhook_api_key;
    this.$<HTMLInputElement>("show-hud").checked = c.show_hud;
    this.$<HTMLInputElement>("show-ids").checked = c.show_track_ids;
    this.$<HTMLInputElement>("show-conf").checked = c.show_confidence;
    this.$<HTMLSelectElement>("device-select").value = c.inference_device;
    this.$<HTMLSelectElement>("storage-mode").value = c.storage_mode;
  }
}
